'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { dispatch, type RootState } from '@/store/store';
import { useAppFilters } from '@/hooks';
import { useFetchReports } from '@/hooks/AssetsStudioPage/shared/useFetchReports';
import { useParams } from 'react-router-dom';
import { setHasOilReport } from '@/store/slices/assetStudioSlice';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
// import { Top20AlarmsChartCard } from '../../../shared/Top20AlarmsChartCard'; // REMOVED - integrated into sensors
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { useLazyGetExternalAlarmsQuery } from '@/store/api/alarmsApi';
import { format } from 'date-fns';
import { DIAGNOSTICS_DATE_FORMAT } from '@/const';
import { useManagedGraphs, useCombinedLoadingState, useVerticalLines, useIntervalLines, useDialogState } from '@/hooks/shared/DataLayers';
import { transformAlarmsData } from './utils/transform-alarms-data';
import { getVerticalLinesFromSpectrumExpandableLegend } from '@/utils/expandableLegend.utils';
import { IntervalDialog } from './components/charts-section/intervals/IntervalDialog';
import {
  extractGraphConfigurations,
  initializeEnabledGraphs,
  createInitializationKey,
} from './utils/initialize-graphs';
import { useSensorQueryParams, useEnhancedQueryParams } from './hooks/use-sensor-query-params';
import { createDataLayersConfig, DATA_LAYERS_RENDER_ORDER } from './config/data-layers-config';
import { SENSOR_FUSION_DISPLAY_MODES } from './config/sensor-fusion-display-modes';
import { createSensorHexColorMap } from './utils/sensorDataColorMap';

interface SensorDataProps {
  asset: any;
  components: any[];
  diagnostics: any[];
  config: any;
  activeOperationalModes: string[];
  activeDataLayers: string[];
  onLoadingStateChange?: (isLoading: boolean) => void; // NEW: Loading state callback
  triggerRefresh?: number; // NEW: Trigger refresh when tab becomes active
  displayMode?: 'sensor-fusion' | 'spectrum' | 'waveform'; // NEW: Display mode for CSS-based show/hide
  spectrumConfig?: any; // NEW: Spectrum config for NewSpectrum component
  selectedMarkerTimestamp?: string | null; // NEW: Selected marker timestamp from Spectrum for highlighting
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void; // NEW: Setter for selected marker timestamp
  selectedTimestampsSet?: Set<string>; // Selected timestamps in focus mode
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void; // Set selected timestamps in focus mode
  sensorFusionVerticalLinesState?: ReturnType<typeof useVerticalLines>; // SensorFusion's own vertical lines state from parent
  spectrumVerticalLinesState?: ReturnType<typeof useVerticalLines>; // Spectrum's vertical lines state from parent
  benchmarkLineBySection?: Record<string, string | null>; // Shared benchmark state from parent
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>; // Setter for benchmark state
}
//@TODO: CONSOLIDATE AND REMOVE CODE DUPLICATIONS!
export const SensorFusion = ({
  asset,
  components,
  diagnostics,
  config,
  activeOperationalModes,
  activeDataLayers,
  onLoadingStateChange, // NEW: Callback to communicate loading state to parent
  triggerRefresh = 0,
  displayMode = 'sensor-fusion', // NEW: Display mode for CSS-based visibility
  spectrumConfig, // NEW: Spectrum config for NewSpectrum component
  selectedMarkerTimestamp, // NEW: Selected marker timestamp from Spectrum for highlighting
  setSelectedMarkerTimestamp, // NEW: Setter for selected marker timestamp
  selectedTimestampsSet, // Selected timestamps in focus mode
  setSelectedTimestampsSet, // Set selected timestamps in focus mode
  sensorFusionVerticalLinesState, // SensorFusion's own vertical lines state from parent
  spectrumVerticalLinesState, // Spectrum's vertical lines state from parent
  benchmarkLineBySection, // Shared benchmark state from parent
  setBenchmarkLineBySection, // Setter for benchmark state
}: SensorDataProps) => {
  const { isMobileAsset } = useActiveAssetsType();
  const [, setTriggerSensorReset] = useState(false);
  const [, setTriggerOilReset] = useState(false);
  const [, setTriggerCoolantReset] = useState(false);
  const [, setTriggerAlarmReset] = useState(false);
  const [forceDataRefresh, setForceDataRefresh] = useState(0);

  // Global loading sensors state - synchronized across all legends in all sections
  const [loadingSensors, setLoadingSensors] = useState<Set<string>>(new Set());

  // Synced tool states for markers/harmonics/sideBands across all sections
  const [markerSelectionModeByChart, setMarkerSelectionModeByChart] = useState<Record<string, boolean>>({});
  const [harmonicSelectionModeByChart, setHarmonicSelectionModeByChart] = useState<Record<string, boolean>>({});
  const [sideBandsSelectionModeByChart, setSideBandsSelectionModeByChart] = useState<Record<string, boolean>>({});
  const [customLineModeByChart, setCustomLineModeByChart] = useState<Record<string, string | null>>({});

  const { siteId, id: assetId} = useParams();
  const assetName = asset?.asset_name;
  const { selectedComponent: selectedComponentId } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const { selectedComponentObj } = useSelector((state: RootState) => state.assetsStudio);

  // Fetch alarm data when alarms are enabled (moved up to avoid using before declaration)
  // ONLY FETCH FOR MOBILE ASSETS (alarms only exist for mobile)
  const shouldFetchAlarms = isMobileAsset;

  // Use the external alarms API like in diagnostics
  const [fetchExternalAlarms, { data: alarmsResponse, isLoading: alarmsLoading = false, isFetching: alarmsFetching }] =
    useLazyGetExternalAlarmsQuery();

  // Fetch alarms when conditions are met
  useEffect(() => {
    // Check alarm fetch conditions

    if (shouldFetchAlarms && from && to && assetId) {
      const fromDateISO = format(new Date(from), DIAGNOSTICS_DATE_FORMAT);
      const toDateISO = format(new Date(to), DIAGNOSTICS_DATE_FORMAT);

      const fetchParams = {
        metric: 'count',
        top_ranked: 20,
        component_ids: selectedComponentId && !selectedComponentId.includes('all') ? [selectedComponentId] : [],
        from_date: fromDateISO,
        to_date: toDateISO,
        group_by: 'alarm_code',
        priority: ['high', 'medium', 'low'], // Include all priorities
        asset_ids: [assetId],
        severity: ['critical', 'error', 'warning', 'info'], // All severities
        alarm_interface: [],
        add_metadata: true,
      };

      // Fetch external alarms with the prepared params
      fetchExternalAlarms(fetchParams)
        .unwrap()
        .then((response) => {
          // Process alarm response if needed
          return response;
        })
        .catch(() => {
          // Handle alarm API errors silently
        });
    } else {
      // Skip fetching - conditions not met
    }
  }, [shouldFetchAlarms, from, to, assetId, selectedComponentId, fetchExternalAlarms, activeDataLayers, isMobileAsset]);

  // Use combined loading state hook
  const {
    handleSensorLoadingStateChange,
    handleOilLoadingStateChange,
    handleCoolantLoadingStateChange,
    loadingStates,
  } = useCombinedLoadingState({
    onLoadingStateChange,
    selectedComponentId,
    selectedPart: null,
    alarmsLoading,
    alarmsFetching,
  });

  // Trigger data refresh when tab becomes active
  const prevTriggerRefreshRef = useRef(triggerRefresh);
  useEffect(() => {
    if (triggerRefresh !== prevTriggerRefreshRef.current && triggerRefresh > 0) {
      // Tab became active, forcing data refresh
      setForceDataRefresh((prev) => prev + 1);
      prevTriggerRefreshRef.current = triggerRefresh;
    }
  }, [triggerRefresh]);

  const selectedComponent = useMemo(
    () => components?.find((component) => component?.component_id === selectedComponentId),
    [components, selectedComponentId]
  );

  const { oilReportRecap: oilReportsRecapData } = useFetchReports({
    asset_name: asset?.asset_name,
    site_id: siteId,
    component_id: selectedComponent?.component_id,
  });

  const baseTitle = selectedComponent?.component_ui_name || selectedComponent?.component_name;
  const stableOperationalModesRef = useRef<string[]>([]);
  useEffect(() => {
    const current = JSON.stringify(activeOperationalModes);
    const previous = JSON.stringify(stableOperationalModesRef.current);
    if (current !== previous) {
      stableOperationalModesRef.current = activeOperationalModes;
    }
  }, [activeOperationalModes]);
  // Initialize ref once
  const isFullPullRef = useRef(false);

  // Always update latest value in render phase
  if (Array.isArray(activeOperationalModes)) {
    isFullPullRef.current = activeOperationalModes.includes('full_pull');
  }

  const isFullPull = isFullPullRef.current;
  const queryParams = useSensorQueryParams({
    siteId,
    assetId,
    assetName,
    selectedComponentId,
    isMobileAsset,
    from,
    to,
    isFullPull,
  });

  // Transform REAL alarm data from API
  const alarmGraphsData = useMemo(() => {
    return transformAlarmsData(alarmsResponse);
  }, [alarmsResponse]);

  useEffect(() => {
    dispatch(setHasOilReport(oilReportsRecapData && Object.keys(oilReportsRecapData).length > 0));
  }, [oilReportsRecapData]);

  // Extract graph configurations from config - memoized to prevent re-creation
  const { sensorGraphs, oilGraphs, coolantGraphs, alarmGraphs } = useMemo(
    () => extractGraphConfigurations(config),
    [JSON.stringify(config?.graphs?.map((g: any) => g.id))]
  );

  const initializationKeyRef = useRef<string>('');

  // State for managing legend items visibility across all graphs
  // Initialize from the actual graph legend states
  const [legendItemStates, setLegendItemStates] = useState<Record<string, boolean>>(() => {
    const initialStates: Record<string, boolean> = {};

    [...sensorGraphs, ...alarmGraphs, ...oilGraphs, ...coolantGraphs].forEach((graph) => {
      graph.legend?.forEach((item: any) => {
        const enabled = item.enabled !== false;
        initialStates[item.id] = enabled;
      });
    });
    return initialStates;
  });

  // Function to toggle legend item visibility
  const toggleLegendItem = useCallback((itemId: string, enabled: boolean) => {
    console.log('[ALARM_TOGGLE] Legend item toggled:', JSON.stringify({ itemId, enabled }, null, 2));
    setLegendItemStates((prev) => {
      const newState = { ...prev, [itemId]: enabled };
      console.log('[ALARM_TOGGLE] legendItemStates updated:', JSON.stringify(newState, null, 2));
      return newState;
    });
  }, []);

  // Store reference to sensor toggle handlers from containers
  const sensorToggleHandlersRef = useRef<Record<string, (sectionId: string, sensorName: string) => void>>({});

  // Store sensor data from containers in state so it triggers re-renders
  const [registeredSensorData, setRegisteredSensorData] = useState<{
    enabledSensors: Record<string, Record<string, boolean>>;
    sensorColors: Record<string, Record<string, string>>;
  }>({
    enabledSensors: {},
    sensorColors: {},
  });

  // Focus mode state - Map of sectionId -> focused sensor name for that section
  const [focusedSensors, setFocusedSensors] = useState<Record<string, string>>({});
  const savedEnabledSensorsRef = useRef<Record<string, Record<string, boolean>> | null>(null);

  // Compute global mode: 'focus' if ANY section is in focus mode, otherwise 'multi'
  const globalLegendSyncMode: 'multi' | 'focus' = useMemo(() => {
    const hasAnyFocus = Object.keys(focusedSensors).length > 0;
    return hasAnyFocus ? 'focus' : 'multi';
  }, [focusedSensors]);

  const legendSync = {
    // Global mode computed from section-specific focuss
    mode: globalLegendSyncMode,
    // Toggle mode - switches between focus and multi mode
    toggleMode: () => {

      if (globalLegendSyncMode === 'focus') {
        // Exit focus mode - clear all focuss to return to multi mode
        setFocusedSensors({});
      } else {
        // Enter focus mode - focus the first enabled sensor in each section
        const newFocuss: Record<string, string> = {};

        // For each data layer (section), find the first enabled sensor
        Object.keys(registeredSensorData.enabledSensors).forEach((sectionId) => {
          const enabledSensors = registeredSensorData.enabledSensors[sectionId];
          const firstEnabledSensor = Object.keys(enabledSensors).find(
            (sensorName) => enabledSensors[sensorName] === true
          );

          if (firstEnabledSensor) {
            newFocuss[sectionId] = firstEnabledSensor;
          }
        });

        setFocusedSensors(newFocuss);
      }
    },
    getSavedState: () => savedEnabledSensorsRef.current,
    clearSavedState: () => { savedEnabledSensorsRef.current = null; },
  };

  const handleFocus = useCallback((sensorName: string, sectionId?: string) => {

    // CRITICAL: If no sectionId provided, cannot do section-specific focus
    if (!sectionId) {
      return;
    }

    // If clicking the currently focused sensor in THIS section (EyeOff), exit focus for this section
    const currentFocusForSection = focusedSensors[sectionId];
    const isTogglingOff = currentFocusForSection === sensorName;

    if (isTogglingOff) {
      setFocusedSensors(prev => {
        const newState = { ...prev };
        delete newState[sectionId];
        return newState;
      });

      // Section-specific focus removed - no global mode change needed
      return;
    }


    // Store focus per section - multiple sections can have focuss simultaneously
    setFocusedSensors(prev => ({
      ...prev,
      [sectionId]: sensorName,
    }));

    // Auto-select all timestamps when entering focus mode
    // Get timestamps from spectrumVerticalLinesState (Spectrum markers) or sensorFusionVerticalLinesState
    const verticalLines = spectrumVerticalLinesState?.verticalLines || sensorFusionVerticalLinesState?.verticalLines || [];
    if (setSelectedTimestampsSet && verticalLines.length > 0) {
      const allTimestampIds = new Set<string>();
      verticalLines.forEach((line: any) => {
        if (line.id) {
          allTimestampIds.add(line.id);
        }
        // Also add ISO timestamp for compatibility
        if (line.timestamp) {
          const timestampISO = typeof line.timestamp === 'number'
            ? new Date(line.timestamp).toISOString()
            : line.timestamp instanceof Date
              ? line.timestamp.toISOString()
              : line.timestamp;
          allTimestampIds.add(timestampISO);
        }
      });
      setSelectedTimestampsSet(allTimestampIds);
    }
  }, [focusedSensors, spectrumVerticalLinesState?.verticalLines, sensorFusionVerticalLinesState?.verticalLines, setSelectedTimestampsSet]);

  // Track vertical lines count to detect when new lines are added
  const prevVerticalLinesCountRef = useRef<number>(0);

  // Auto-select newly added timestamps when in focus mode
  useEffect(() => {
    const verticalLines = spectrumVerticalLinesState?.verticalLines || sensorFusionVerticalLinesState?.verticalLines || [];
    const currentCount = verticalLines.length;
    const prevCount = prevVerticalLinesCountRef.current;

    // Check if we're in focus mode (any section has a focused sensor)
    const isInFocusMode = Object.keys(focusedSensors).length > 0;

    // Only auto-select when:
    // 1. A new line was added (count increased)
    // 2. We're in focus mode
    // 3. We have the setter available
    if (currentCount > prevCount && isInFocusMode && setSelectedTimestampsSet) {
      // Find the newly added lines (lines that weren't there before)
      // The newest lines are typically at the end of the array
      const newLines = verticalLines.slice(prevCount);

      setSelectedTimestampsSet((prev: Set<string>) => {
        const updated = new Set(prev);
        newLines.forEach((line: any) => {
          if (line.id) {
            updated.add(line.id);
          }
          // Also add ISO timestamp for compatibility
          if (line.timestamp) {
            const timestampISO = typeof line.timestamp === 'number'
              ? new Date(line.timestamp).toISOString()
              : line.timestamp instanceof Date
                ? line.timestamp.toISOString()
                : line.timestamp;
            updated.add(timestampISO);
          }
        });
        return updated;
      });
    }

    // Update the ref after processing
    prevVerticalLinesCountRef.current = currentCount;
  }, [
    spectrumVerticalLinesState?.verticalLines?.length,
    sensorFusionVerticalLinesState?.verticalLines?.length,
    focusedSensors,
    setSelectedTimestampsSet,
  ]);

  // Function to toggle focus mode for a specific section
  const handleToggleFocusModeForSection = useCallback((sectionId: string) => {

    // Check if this section already has a focus
    const hasFocus = focusedSensors[sectionId] !== undefined;

    if (hasFocus) {
      // Exit focus mode for this section
      setFocusedSensors(prev => {
        const newState = { ...prev };
        delete newState[sectionId];
        return newState;
      });
    } else {
      // Enter focus mode for this section - focus the first enabled sensor

      // Find the first enabled sensor in this section
      const enabledSensors = registeredSensorData.enabledSensors[sectionId] || {};
      const firstEnabledSensor = Object.keys(enabledSensors).find(
        (sensorName) => enabledSensors[sensorName] === true
      );

      if (firstEnabledSensor) {
        setFocusedSensors(prev => ({
          ...prev,
          [sectionId]: firstEnabledSensor,
        }));
      } else {
      }
    }
  }, [focusedSensors, registeredSensorData.enabledSensors]);

  // Function to register sensor toggle handlers from containers
  const registerSensorToggleHandler = useCallback(
    (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => {
      sensorToggleHandlersRef.current[dataLayer] = handler;
    },
    []
  );

  // Function to register sensor data from containers
  const registerSensorData = useCallback(
    (dataLayer: string, enabledSensors: Record<string, boolean>, sensorColors: Record<string, string>) => {

      setRegisteredSensorData((prev) => ({
        enabledSensors: {
          ...prev.enabledSensors,
          [dataLayer]: enabledSensors,
        },
        sensorColors: {
          ...prev.sensorColors,
          [dataLayer]: sensorColors,
        },
      }));

    },
    []
  );

  // Function to toggle sensor (called from search filters)
  const handleSensorToggleFromSearch = useCallback(
    (sectionId: string, sensorName: string) => {
      // The sectionId is the graph ID, we need to find which data layer it belongs to
      let dataLayer = SENSOR_FUSION_DATA_LAYERS.SENSORS; // Default to sensors

      // Check if this is an alarm graph
      if (sectionId === 'alarm' || sectionId.startsWith('alarm')) {
        dataLayer = SENSOR_FUSION_DATA_LAYERS.ALARMS;
      }
      // Check if it's one of the sensor graphs
      else if (sensorGraphs.some((g: any) => g.id === sectionId)) {
        dataLayer = SENSOR_FUSION_DATA_LAYERS.SENSORS;
      }
      // Check if it's one of the alarm graphs
      else if (alarmGraphs.some((g: any) => g.id === sectionId)) {
        dataLayer = SENSOR_FUSION_DATA_LAYERS.ALARMS;
      }


      const handler = sensorToggleHandlersRef.current[dataLayer];
      if (handler) {
        // Pass the sectionId (graph ID) directly to the handler
        handler(sectionId, sensorName);
      } else {
      }
    },
    [sensorGraphs, alarmGraphs]
  );

  // Function to add sensor (called from search filters for disabled sensors)
  // This just calls the same toggle handler - the container will enable if disabled
  const handleSensorAdditionFromSearch = handleSensorToggleFromSearch;

  const initalEnabled = useMemo(() => {
    const result = initializeEnabledGraphs(sensorGraphs, oilGraphs, coolantGraphs);

    const initKey = createInitializationKey(selectedComponentId, null, sensorGraphs, oilGraphs, coolantGraphs);

    if (initKey !== initializationKeyRef.current) {
      initializationKeyRef.current = initKey;
    }

    return result;
  }, [sensorGraphs, oilGraphs, coolantGraphs, selectedComponentId]);

  // Use generalized hooks for managing graph sections
  const sensorsManager = useManagedGraphs({
    graphs: sensorGraphs,
    initialEnabled: initalEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.SENSORS,
  });

  const oilManager = useManagedGraphs({
    graphs: oilGraphs,
    initialEnabled: initalEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.OIL_REPORT,
  });

  const coolantManager = useManagedGraphs({
    graphs: coolantGraphs,
    initialEnabled: initalEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.COOLANT,
  });

  const alarmsManager = useManagedGraphs({
    graphs: alarmGraphs,
    initialEnabled: {
      [SENSOR_FUSION_DATA_LAYERS.ALARMS]: alarmGraphs
        .filter((g: any) => g.enabled === true) // Only enabled graphs from config
        .map((g: any) => g.id),
    },
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.ALARMS,
  });

  // All state management is now handled by the useManagedGraphs hooks

  const enhancedQueryParams = useEnhancedQueryParams(queryParams);

  // Use vertical lines state from parent if provided, otherwise create local fallback
  const localVerticalLinesState = useVerticalLines();
  const baseVerticalLinesState = sensorFusionVerticalLinesState || localVerticalLinesState;

  // Get vertical lines from Spectrum's expandable legend (linkedSources with drawVerticalLines: true)
  const expandableLegendVerticalLines = useMemo(() => {
    return getVerticalLinesFromSpectrumExpandableLegend(spectrumConfig);
  }, [spectrumConfig]);

  // Store calculated values for expandable legend lines (persists across renders)
  // This ref stores values calculated by useVerticalLineValues for external lines
  const expandableLegendValuesRef = useRef<Record<string, Record<string, number>>>({});

  // Merge base vertical lines with expandable legend vertical lines
  // Also applies stored values to expandable legend lines so they can be used as benchmarks
  const verticalLinesState = useMemo(() => {
    if (expandableLegendVerticalLines.length === 0) {
      return baseVerticalLinesState;
    }

    // Merge the arrays, avoiding duplicates by ID
    const existingIds = new Set(baseVerticalLinesState.verticalLines?.map((l: any) => l.id) || []);
    const newLines = expandableLegendVerticalLines
      .filter(line => !existingIds.has(line.id))
      .map(line => {
        // Apply stored values if available (populated by useVerticalLineValues)
        const storedValues = expandableLegendValuesRef.current[line.id];
        if (storedValues && Object.keys(storedValues).length > 0) {
          return { ...line, values: storedValues };
        }
        return line;
      });

    // Create wrapped updateAllVerticalLineValues that also stores expandable legend values
    const wrappedUpdateAllVerticalLineValues = (
      getValuesAtTimestamp: (timestamp: Date | number, existingValues: Record<string, number>) => Record<string, number>
    ) => {
      // Update base vertical lines as normal
      baseVerticalLinesState.updateAllVerticalLineValues(getValuesAtTimestamp);

      // Also calculate and store values for expandable legend lines
      expandableLegendVerticalLines.forEach(line => {
        const calculatedValues = getValuesAtTimestamp(line.timestamp, line.values || {});
        if (calculatedValues && Object.keys(calculatedValues).length > 0) {
          expandableLegendValuesRef.current[line.id] = calculatedValues;
        }
      });
    };

    return {
      ...baseVerticalLinesState,
      verticalLines: [...(baseVerticalLinesState.verticalLines || []), ...newLines],
      updateAllVerticalLineValues: wrappedUpdateAllVerticalLineValues,
    };
  }, [baseVerticalLinesState, expandableLegendVerticalLines]);

  // Create highlighted version of verticalLines when marker(s) are selected in Spectrum or expanded legend
  const verticalLinesWithHighlight = React.useMemo(() => {
    // Check if we have any selected timestamps (either single or multiple)
    const hasSelectedTimestamps = selectedMarkerTimestamp || (selectedTimestampsSet && selectedTimestampsSet.size > 0);

    if (!hasSelectedTimestamps || !verticalLinesState?.verticalLines) {
      return verticalLinesState;
    }

    // Helper function to normalize timestamps for comparison
    // Backend timestamps have microseconds (6 digits), JS Date has milliseconds (3 digits)
    // Normalize to milliseconds for consistent comparison
    const normalizeTimestamp = (ts: string): string => {
      // Match pattern: YYYY-MM-DDTHH:MM:SS.microsZ and truncate to milliseconds
      const match = ts.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3})\d*Z$/);
      if (match) {
        return match[1] + 'Z';
      }
      return ts;
    };

    // Find the marker(s) matching the selected timestamp(s) and highlight with purple
    const highlightedLines = verticalLinesState.verticalLines.map((line: any) => {
      // Convert line timestamp to ISO string for comparison with Spectrum timestamps
      const lineTimestampISO = line.timestamp instanceof Date
        ? line.timestamp.toISOString()
        : typeof line.timestamp === 'number'
        ? new Date(line.timestamp).toISOString()
        : String(line.timestamp);

      // Normalize for comparison (truncate microseconds to milliseconds)
      const normalizedLineTimestamp = normalizeTimestamp(lineTimestampISO);

      // Check if this timestamp is in the selected set (by ID, timestamp ISO, or normalized timestamp)
      let matches = false;
      if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
        // Check by line ID first (handler stores item IDs, not timestamps)
        if (line.id && selectedTimestampsSet.has(line.id)) {
          matches = true;
        } else if (selectedTimestampsSet.has(lineTimestampISO)) {
          // Check direct timestamp match
          matches = true;
        } else {
          // Check normalized match (for microsecond vs millisecond precision difference)
          for (const selectedTs of selectedTimestampsSet) {
            if (normalizeTimestamp(selectedTs) === normalizedLineTimestamp) {
              matches = true;
              break;
            }
          }
        }
      }

      // If this line matches, apply purple highlight
      if (matches) {
        return {
          ...line,
          color: '#7071F3', // Primary purple color (matching PlotlyChart)
          originalColor: line.originalColor || line.color, // Store original color
        };
      }

      // Restore original color if it was previously highlighted
      if (line.originalColor) {
        return {
          ...line,
          color: line.originalColor,
          originalColor: undefined,
        };
      }

      return line;
    });

    return {
      ...verticalLinesState,
      verticalLines: highlightedLines,
    };
  }, [
    verticalLinesState?.verticalLines?.length,
    verticalLinesState.verticalLines, // Track the actual array reference
    selectedMarkerTimestamp,
    selectedTimestampsSet?.size, // Track Set size for changes
    Array.from(selectedTimestampsSet || []).join(','), // Track actual timestamp values
  ]);
  // Interval lines state
  const { intervalLines, addIntervalLines, removeIntervalLine, clearIntervalLines } = useIntervalLines();

  // Dialog state for intervals
  const {
    showIntervalDialog,
    setShowIntervalDialog,
    handleShowIntervalDialog,
  } = useDialogState();

  // Refs for data layer configuration - must be at component top level (React Rules of Hooks)
  const groupFilterResetRef = useRef<(() => void) | null>(null);
  const initialAlarmLegendStatesRef = useRef<Record<string, boolean> | null>(null);

  // Create MASTER sensor color map - collect ALL sensor names from ALL data layers and assign consistent colors
  const masterSensorColorMap = useMemo(() => {
    const allSensorNames: string[] = [];
    const seen = new Set<string>();

    // Collect sensor names from SensorFusion graphs in order they appear
    sensorGraphs?.forEach((graph: any) => {
      const sensors = graph.sensorUiNames || graph.legend?.map((item: any) => item.displayName || item.name) || [];
      sensors.forEach((name: string) => {
        if (!seen.has(name)) {
          seen.add(name);
          allSensorNames.push(name);
        }
      });
    });

    // Collect sensor names from Spectrum config in order they appear
    if (spectrumConfig?.graphs) {
      spectrumConfig.graphs.forEach((graph: any) => {
        const sensors = graph.sensorUiNames || graph.legend?.map((item: any) => item.displayName || item.name) || [];
        sensors.forEach((name: string) => {
          if (!seen.has(name)) {
            seen.add(name);
            allSensorNames.push(name);
          }
        });
      });
    }

    // Preserve original order - don't sort
    // Use the imported createSensorHexColorMap utility
    return createSensorHexColorMap(allSensorNames);
  }, [sensorGraphs, spectrumConfig?.graphs]);

  // Create declarative data layers configuration - NO MEMOIZATION like main
  const dataLayersConfig = createDataLayersConfig({
    activeDataLayers,
    isMobileAsset,
    assetName,
    diagnostics,
    baseTitle,
    from,
    to,
    sensorGraphs,
    oilGraphs,
    coolantGraphs,
    alarmGraphs,
    sensorsManager,
    oilManager,
    coolantManager,
    alarmsManager,
    enhancedQueryParams,
    forceDataRefresh,
    handleSensorLoadingStateChange,
    handleOilLoadingStateChange,
    handleCoolantLoadingStateChange,
    loadingStates,
    alarmGraphsData,
    selectedComponentId,
    selectedComponentObj,
    setTriggerSensorReset,
    setTriggerOilReset,
    setTriggerCoolantReset,
    setTriggerAlarmReset,
    verticalLinesState: verticalLinesWithHighlight, // Pass highlighted lines for SensorFusion charts
    rawVerticalLinesState: verticalLinesState, // Pass SensorFusion's raw lines for cross-tab access
    spectrumVerticalLinesState, // Pass Spectrum's separate vertical lines state
    benchmarkLineBySection, // Pass shared benchmark state from parent
    setBenchmarkLineBySection, // Pass setter for benchmark state
    config,
    legendItemStates,
    toggleLegendItem,
    onToggleSensor: handleSensorToggleFromSearch,
    onAddSensor: handleSensorAdditionFromSearch,
    registerSensorToggleHandler,
    registerSensorData,
    allEnabledSensors: registeredSensorData.enabledSensors,
    allSensorColors: registeredSensorData.sensorColors,
    masterSensorColorMap, // Pass master color map for consistent colors across all data layers
    isSensorLoading: () => false, // Simple loading function - always return false
    // Interval lines props
    intervalLines,
    onOpenIntervalDialog: handleShowIntervalDialog,
    onClearIntervalLines: clearIntervalLines,
    removeIntervalLine,
    // Display mode and spectrum props
    displayMode, // Pass actual displayMode so layer configs can read it
    asset,
    components,
    spectrumConfig,
    activeOperationalModes,
    // Refs for data layer configuration (React Rules of Hooks compliance)
    groupFilterResetRef,
    initialAlarmLegendStatesRef,
    // Global loading sensors state
    loadingSensors,
    setLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => new Set(prev).add(sensorName));
    },
    clearLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sensorName);
        return newSet;
      });
    },
    // Synced tool selection states (for syncTools feature)
    markerSelectionModeByChart,
    setMarkerSelectionModeByChart,
    harmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart,
    customLineModeByChart,
    setCustomLineModeByChart,
    // Focus mode props
    legendSync,
    focusedSensors,
    onFocus: handleFocus,
    // Selected marker timestamp for highlighting in SensorFusion charts
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    // Selected timestamps set for focus mode multi-select
    selectedTimestampsSet,
    setSelectedTimestampsSet,
  });


  // Get current display mode config
  const currentModeConfig = SENSOR_FUSION_DISPLAY_MODES[displayMode];

  // Map data layers to components with CSS-based visibility control
  // Always render but use display:none for hidden layers to prevent data reloading
  const dataLayersToComponent = dataLayersConfig.reduce((acc, layer) => {
    // Determine visibility:
    // 1. Check if layer is explicitly hidden via hiddenLayers
    const isExplicitlyHidden = currentModeConfig?.hiddenLayers?.includes(layer.id) ?? false;

    // 2. Check visibility based on mode config:
    //    - If visibleLayers is empty → use layer.condition() (respects metadata - sensor-fusion mode)
    //    - If visibleLayers has items → check if layer is in list (override - spectrum mode)
    const meetsVisibilityRules =
      !currentModeConfig?.visibleLayers || currentModeConfig.visibleLayers.length === 0
        ? layer.condition?.() ?? true
        : currentModeConfig.visibleLayers.includes(layer.id);

    // Final visibility: meets rules AND not explicitly hidden
    const isVisible = meetsVisibilityRules && !isExplicitlyHidden;

    // Always render with display:none to keep mounted (prevents data reload on mode switch)
    acc[layer.id] = (
      <div key={layer.id} style={{ display: isVisible ? 'block' : 'none' }}>
        {layer.component()}
      </div>
    );
    return acc;
  }, {} as Record<string, React.ReactNode>);

  // Trigger Plotly resize when display mode changes (especially when spectrum becomes visible)
  useEffect(() => {
    // Give the DOM time to update visibility before resizing
    const timeout = setTimeout(() => {
      if (window.Plotly && displayMode === 'spectrum') {
        // Find all Plotly chart elements in the spectrum layer and resize them
        const spectrumCharts = document.querySelectorAll('[id^="chart-spectrum-"]');
        spectrumCharts.forEach((chart) => {
          try {
            window.Plotly.Plots.resize(chart as HTMLElement);
          } catch (e) {
          }
        });
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [displayMode]);

  return (
    <>
      {dataLayersToComponent[SENSOR_FUSION_DATA_LAYERS.AI_TREND]}
      <div className="flex flex-col gap-4 max-h-full">
        {DATA_LAYERS_RENDER_ORDER.filter((id) => id !== SENSOR_FUSION_DATA_LAYERS.AI_TREND)
          .map((id) =>
            dataLayersToComponent[id] ? <React.Fragment key={id}>{dataLayersToComponent[id]}</React.Fragment> : null
          )
          .filter(Boolean)}
      </div>
      <IntervalDialog
        open={showIntervalDialog}
        onClose={() => setShowIntervalDialog(false)}
        intervalLines={intervalLines}
        onAddIntervalLines={addIntervalLines}
        onRemoveIntervalLine={removeIntervalLine}
      />
    </>
  );
};
